import { useCallback, useEffect, useRef, useState } from 'react';

export interface TrainingExerciseSelectionTarget {
  blockId: string;
  exerciseId: string;
}

const isModifierKey = (event: KeyboardEvent) => event.key === 'Control' || event.key === 'Meta';

const isTypingTarget = (event: KeyboardEvent) => {
  const target = event.target;

  return target instanceof HTMLElement
    && Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
};

export const useTrainingExerciseMultiSelect = (
  enabled: boolean,
  onSelectModeEnd?: (selected: TrainingExerciseSelectionTarget[]) => void,
  locked = false,
) => {
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState<TrainingExerciseSelectionTarget[]>([]);
  const selectedTargetsRef = useRef(selectedTargets);
  const lastAnchorRectRef = useRef<DOMRect | null>(null);
  const onSelectModeEndRef = useRef(onSelectModeEnd);
  const lockedRef = useRef(locked);
  selectedTargetsRef.current = selectedTargets;
  onSelectModeEndRef.current = onSelectModeEnd;
  lockedRef.current = locked;

  const toggleTarget = useCallback((blockId: string, exerciseId: string, anchorRect: DOMRect) => {
    lastAnchorRectRef.current = anchorRect;
    setSelectedTargets((currentTargets) => {
      if (currentTargets.some((target) => target.exerciseId === exerciseId)) {
        return currentTargets.filter((target) => target.exerciseId !== exerciseId);
      }

      return [...currentTargets, { blockId, exerciseId }];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedTargets([]);
    lastAnchorRectRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsSelectMode(false);
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isModifierKey(event) || isTypingTarget(event) || event.repeat || lockedRef.current) {
        return;
      }

      setIsSelectMode(true);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!isModifierKey(event)) {
        return;
      }

      setIsSelectMode(false);

      if (lockedRef.current) {
        return;
      }

      onSelectModeEndRef.current?.(selectedTargetsRef.current);
    };

    const handleBlur = () => {
      setIsSelectMode(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [enabled]);

  useEffect(() => {
    if (locked) {
      setIsSelectMode(false);
    }
  }, [locked]);

  return {
    isSelectMode,
    selectedTargets,
    selectedTargetsRef,
    lastAnchorRectRef,
    toggleTarget,
    clearSelection,
  };
};
